import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  BackButton,
  Box,
  HeadingStyled,
  LazyImage,
  SpanStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../../components/styled/Elements";
import InfoData from "../../../../components/common/infoData/InfoData";
import Breadcrumbs from "../../../../components/admin/Breadcrumbs/Breadcrumbs";
import { FormDataContainer, SettingsWrapper } from "../../billing/styled";
import { PdfIcon, PlayIcon } from "../../../../components/styled/AllImages";
import CustomTile from "../../../../components/common/statusTiles/CustomTile";
import ListTable from "../../../../components/common/tables/ListTable";
import {
  invoiceListColumns,
  quotationListColumns,
} from "../../../../helpers/adminConstants/TableConstants";
import ProductsApi from "../../../../services/api/productsApi";
import { useUser } from "../../../../context/userContext";

const BASE_URL = process.env.REACT_APP_ASSET_URL;

const ProductDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { user } = useUser();

  const { id, prod_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const { data, code } = await ProductsApi.fetchProduct(id, prod_id);
        if (code === 200 && data) {
          setProduct({
            ...data,
            quotations: data?.quotations?.map((item) => ({
              ...item,
              client: language === "en" ? item?.client_en : item?.client_ar,
            })),
            invoices: data?.invoices?.map((item) => ({
              ...item,
              client: language === "en" ? item?.client_en : item?.client_ar,
            })),
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, language]);

  return (
    <SettingsWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("view_product")}
            onClick={() => navigate(`/products-categories/${id}/products`)}
          />
          <Breadcrumbs />
        </Box>
      </Box>

      <Box className="content-container">
        {loading ? (
          <ThemeLoader />
        ) : (
          <Row className="mt-3">
            <Col md={12}>
              <FormDataContainer className="dataContainer pb-3">
                <InfoData label="name_en" text={product?.name_en} />
                <InfoData label="name_ar" text={product?.name_ar} />
                <InfoData
                  label="unit_price"
                  text={`${
                    user?.organization?.currency?.stringCode ||
                    user?.organization?.currency?.stringCode ||
                    t("sar")
                  } ${product?.price}`}
                />
                <InfoData
                  label="unit_of_measure"
                  text={product?.unit_of_measurement}
                />
                <InfoData label="description" text={product?.description} />

                <InfoData
                  label="catalog"
                  render={() =>
                    product?.catalog?.map((file, i) => (
                      <Box key={i} className="d-flex align-items-center gap-2">
                        <LazyImage src={PdfIcon} />
                        <SpanStyled>{file.split("/").pop()}</SpanStyled>
                      </Box>
                    ))
                  }
                />

                <InfoData
                  label="others"
                  render={() =>
                    product?.others?.map((file, i) => (
                      <Box key={i} className="d-flex align-items-center gap-2">
                        <LazyImage src={PdfIcon} />
                        <SpanStyled>{file.split("/").pop()}</SpanStyled>
                      </Box>
                    ))
                  }
                />

                <Box className="innerFoHead">
                  <SpanStyled weight="600" size="16px" color="var(--text)">
                    {t("images")}
                  </SpanStyled>
                </Box>
                <Box className="px-4 d-flex flex-wrap gap-3">
                  {product?.images?.map((img, i) => (
                    <Box key={i} className="img-box">
                      <LazyImage
                        src={`${BASE_URL}/${img}`}
                        alt={`image-${i}`}
                      />
                    </Box>
                  ))}
                </Box>

                <Box className="innerFoHead">
                  <SpanStyled weight="600" size="16px" color="var(--text)">
                    {t("Videos")}
                  </SpanStyled>
                </Box>
                <Box className="px-4 d-flex flex-wrap gap-3">
                  {product?.videos?.map((vid, i) => (
                    <Box key={i} className="img-box">
                      <video
                        src={`${BASE_URL}/${vid}`}
                        muted
                        playsInline
                        preload="metadata"
                        style={{
                          width: "220px",
                          height: "101px",
                          borderRadius: 10,
                        }}
                      />
                      <Box className="icon-box">
                        <LazyImage src={PlayIcon} className="vid-icon" />
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box className="mt-4 table-main">
                  <HeadingStyled size="20px" weight="600" color="var(--text)">
                    {t("sales_records")}
                  </HeadingStyled>
                  <ListTable
                    options={[]}
                    rows={product?.invoices}
                    columns={invoiceListColumns}
                    perPage={20}
                  />
                </Box>

                <Box className="mt-4 table-main">
                  <HeadingStyled size="20px" weight="600" color="var(--text)">
                    {t("quotations_records")}
                  </HeadingStyled>
                  <ListTable
                    options={[]}
                    rows={product?.quotations}
                    columns={quotationListColumns}
                    perPage={20}
                  />
                </Box>
              </FormDataContainer>
            </Col>
          </Row>
        )}
      </Box>
    </SettingsWrapper>
  );
};

export default ProductDetails;
